import React, { useEffect, useState, useContext, useMemo } from "react";
import api from "../api/axios";
import { AuthContext } from "../context/AuthContext";
import { FaCertificate, FaCheckCircle, FaClipboardList } from "react-icons/fa";

export default function Overview() {
  const { user, isAuthenticated, authenticationInProgress, loading } = useContext(AuthContext);
  const [myCerts, setMyCerts] = useState([]);
  const [myQuiz, setMyQuiz] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only make API calls if user is fully authenticated and not in progress
    if (!isAuthenticated || authenticationInProgress || loading || !user) {
      return;
    }

    const fetchData = async () => {
      setDataLoading(true);
      setError(null);
      
      try {
        const [cRes, qRes] = await Promise.all([
          api.get("/certs/my"),
          api.get("/quiz/my"),
        ]);
        setMyCerts(cRes.data || []);
        setMyQuiz(qRes.data || []);
      } catch (err) {
        console.error("Error fetching overview data:", err);
        setError("Failed to load data. Please refresh the page.");
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, authenticationInProgress, loading, user]);

  const stats = useMemo(() => {
    const certPoints = myCerts.reduce((s, c) => s + (c.points || 0), 0);
    const quizPoints = myQuiz.reduce((s, q) => s + (q.points || 0), 0);
    const totalXP = certPoints + quizPoints;

    const registeredCount = myCerts.filter((c) => c.status === "Registered").length;
    const completedCount = myCerts.filter((c) => c.status === "Completed").length;

    return {
      certPoints,
      quizPoints,
      totalXP,
      registeredCount,
      completedCount,
      quizzesTaken: myQuiz.length,
    };
  }, [myCerts, myQuiz]);

  // Show loading state while authentication is in progress
  if (loading || authenticationInProgress) {
    return (
      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div style={loadingCard}>
          <div style={loadingSpinner}></div>
          <div style={loadingText}>Loading your data...</div>
        </div>
      </div>
    );
  }

  // Show error state if not authenticated
  if (!isAuthenticated || !user) {
    return (
      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div style={errorCard}>
          <div style={errorText}>Please log in to view your overview.</div>
        </div>
      </div>
    );
  }

  // Show error if data fetch failed
  if (error) {
    return (
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div style={errorCard}>
              <div style={errorText}>{error}</div>
              <button 
                style={retryButton} 
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Show loading overlay for data fetching */}
      {dataLoading && (
        <div style={loadingOverlay}>
          <div style={loadingSpinner}></div>
        </div>
      )}

      {/* ================= TOP STATS ================= */}
      <div className="row g-3">

        {/* Total XP */}
        <div className="col-md-4">
          <div className="card" style={cardStyle}>
            <div style={cardTitle}>TOTAL XP</div>
            <div style={cardValue}>{stats.totalXP}</div>
            <div style={cardSub}>
              Cert: {stats.certPoints} • Quiz: {stats.quizPoints}
            </div>
          </div>
        </div>

        {/* Certifications */}
        <div className="col-md-4">
          <div className="card" style={cardStyle}>
            <div style={cardTitle}>CERTIFICATIONS</div>
            <div style={cardValue}>
              {stats.completedCount}/{stats.completedCount + stats.registeredCount}
            </div>
            <div style={cardSub}>Completed / Total Registered</div>
          </div>
        </div>

        {/* Quizzes */}
        <div className="col-md-4">
          <div className="card" style={cardStyle}>
            <div style={cardTitle}>QUIZZES TAKEN</div>
            <div style={cardValue}>{stats.quizzesTaken}</div>
            <div style={cardSub}>Weekly quiz attempts so far</div>
          </div>
        </div>
      </div>

      {/* ================= CERTS + QUIZ ================= */}
      <div className="mt-4 row g-3">

        {/* ======== RECENT CERTS ======== */}
        <div className="col-lg-6">
          <div className="card" style={cardStyle}>
            <div style={cardTitle}>
              <FaCertificate style={{ marginRight: 8 }} /> Recent Certifications
            </div>

            <div style={scrollArea}>
              {myCerts.slice(0, 10).map((c) => (
                <div
                  key={c.certId}
                  style={certRow}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <div style={certName}>{c.certId}</div>

                    <div style={{ marginTop: 2 }}>
                      {c.status === "Completed" ? (
                        <span style={badgeCompleted}>Completed</span>
                      ) : (
                        <span style={badgeRegistered}>Registered</span>
                      )}
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={certXP}>{c.points} XP</div>
                    <div style={certDate}>
                      {c.completedDate || ""}
                    </div>
                  </div>
                </div>
              ))}

              {myCerts.length === 0 && (
                <div style={emptyText}>
                  You have not registered for any certifications yet.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ======== RECENT QUIZ ======== */}
        <div className="col-lg-6">
          <div className="card" style={cardStyle}>
            <div style={cardTitle}>
              <FaClipboardList style={{ marginRight: 8 }} /> Recent Quiz Attempts
            </div>

            <div style={scrollArea}>
              {myQuiz.slice(0, 10).map((q) => (
                <div
                  key={q.attemptKey}
                  style={certRow}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <div style={quizName}>{q.provider} — {q.weekId}</div>

                    <div style={quizCorrect}>
                      {q.correct}/{q.total} correct
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={quizXP}>{q.points} XP</div>
                    <div style={quizDate}>
                      {q.takenAt ? new Date(q.takenAt).toLocaleDateString() : ""}
                    </div>
                  </div>
                </div>
              ))}

              {myQuiz.length === 0 && (
                <div style={emptyText}>
                  You haven’t taken any quizzes yet. Head to Quiz Arena!
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

    </div>
  );
}

/* ================= STYLES ================= */

const cardStyle = {
  borderRadius: 14,
  background: "rgba(3,19,40,0.92)",
  border: "1px solid rgba(130,200,255,0.35)",
  padding: 16,
};

const cardTitle = {
  fontSize: 14,
  fontWeight: 600,
  color: "#8ecbff",
  letterSpacing: 0.5,
  display: "flex",
  alignItems: "center",
};

const cardValue = {
  fontSize: 32,
  fontWeight: 800,
  color: "#eaf6ff",
  marginTop: 6,
};

const cardSub = {
  fontSize: 12,
  color: "#9fbfff",
};

/* Scroll area for lists */
const scrollArea = {
  maxHeight: 400,
  overflowY: "auto",
  marginTop: 12,
  paddingRight: 10,
};

/* Certificate row */
const certRow = {
  padding: "10px 0",
  borderBottom: "1px solid rgba(255,255,255,0.05)",
  fontSize: 14,
};

const certName = {
  fontWeight: 700,
  color: "#eaf6ff",
};

const certXP = {
  color: "#eaf6ff",
  fontWeight: 700,
};

const certDate = {
  color: "#8ecbff",
  fontSize: 11,
};

const badgeCompleted = {
  background: "rgba(0,200,0,0.18)",
  color: "#4cff4c",
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 8,
};

const badgeRegistered = {
  background: "rgba(255,199,0,0.12)",
  color: "#ffdd66",
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 8,
};

/* Quiz styles */
const quizName = {
  fontWeight: 700,
  color: "#eaf6ff",
};

const quizCorrect = {
  color: "#8ecbff",
  fontSize: 13,
};

const quizXP = {
  color: "#eaf6ff",
  fontWeight: 700,
};

const quizDate = {
  color: "#8ecbff",
  fontSize: 11,
};

/* Empty state */
const emptyText = {
  fontSize: 13,
  color: "#8ecbff",
  paddingTop: 5,
};

/* Loading states */
const loadingCard = {
  background: "rgba(3, 18, 40, 0.85)",
  border: "1px solid #2563eb55",
  borderRadius: 16,
  padding: 30,
  textAlign: "center",
  backdropFilter: "blur(6px)",
};

const loadingSpinner = {
  width: 45,
  height: 45,
  border: "5px solid rgba(255,255,255,0.2)",
  borderTopColor: "#3b82f6",
  borderRadius: "50%",
  margin: "0 auto 20px auto",
  animation: "spin 0.8s linear infinite",
};

const loadingText = {
  color: "#9ecbff",
  fontSize: 16,
  fontWeight: 500,
};

const loadingOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};

/* Error states */
const errorCard = {
  background: "rgba(3, 18, 40, 0.85)",
  border: "1px solid #dc354555",
  borderRadius: 16,
  padding: 30,
  textAlign: "center",
  backdropFilter: "blur(6px)",
};

const errorText = {
  color: "#ff9999",
  fontSize: 16,
  fontWeight: 500,
  marginBottom: 20,
};

const retryButton = {
  background: "#3b82f6",
  color: "white",
  border: "none",
  borderRadius: 8,
  padding: "10px 20px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  transition: "background-color 0.2s",
};
